import {autoUpdate, flip, offset, shift, useFloating} from "@floating-ui/react";
import classNames from "classnames";
import {useEffect, useRef, useState} from "react";
import {IoFilter} from "react-icons/io5";

import {Button} from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";

import styles from "./SortMenu.module.scss";

export interface SortMenuProps<T extends string = string> {
  sortBy: T;
  onSortChange: (sortBy: T) => void;
  options: Array<{value: T; label: string}>;
  variant?: "default" | "inspire";
}

export const SortMenu = <T extends string>({sortBy, onSortChange, options, variant = "default"}: SortMenuProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isInspire = variant === "inspire";

  const {refs, floatingStyles} = useFloating({
    open: isOpen,
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({padding: 8})],
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (value: T) => {
    onSortChange(value);
    setIsOpen(false);
  };

  return (
    <div className={styles.sortMenu} ref={dropdownRef}>
      <Button
        ref={isInspire ? refs.setReference : undefined}
        variant="sortBy"
        size="extraSmall"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon icon={IoFilter} display="inline" removeDivDimensions />
        Sort By
      </Button>

      {isOpen && (
        <div
          ref={isInspire ? refs.setFloating : undefined}
          className={classNames(styles.sortMenuDropdown, {[styles.floating]: isInspire})}
          style={isInspire ? floatingStyles : undefined}
        >
          {options.map((option) => (
            <Button
              key={option.value}
              variant="dropdownMenuItem"
              className={classNames({[styles.active]: sortBy === option.value})}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
              {sortBy === option.value && <span className={styles.checkmark}>✓</span>}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
